// How each project is actually put together, keyed by project id.
//
// Optional by design: a project with no record here renders exactly as it did
// before, with no toggle. Five projects are deliberately absent — Esorpa, the
// Cricut wishlist plugin and the three Strapi plugins are each one box and a
// database, and a diagram of that is filler.
//
// Three rules, all enforced by architectures.test.js:
//   - an edge may only join adjacent layers
//   - no layer may hold more than four nodes, and no label more than 22 chars
//   - no node declares a `tech`, and no label names a product
//
// The last one is the disclosure decision: these are client systems, and the
// diagrams describe roles, not the products behind them.
// `kind: "external"` marks a third-party service and draws dashed.

export const architectures = {
  "secret-sale": {
    layers: [
      { label: "Client", nodes: [
          { id: "store", label: "Storefront" },
          { id: "pos", label: "Admin & POS" },
          { id: "camp", label: "Campaign CMS" } ] },
      { label: "Service", nodes: [{ id: "api", label: "Commerce API" }] },
      { label: "Data", nodes: [
          { id: "pg", label: "Relational store" },
          { id: "redis", label: "Cache" },
          { id: "meili", label: "Search index" },
          { id: "pay", label: "Payment gateways", kind: "external" } ] },
    ],
    edges: [
      ["store", "api"], ["pos", "api"], ["camp", "api"],
      ["api", "pg"], ["api", "redis"], ["api", "meili", "index"],
      ["api", "pay", "checkout"],
    ],
  },

  "rectron-summit": {
    layers: [
      { label: "Client", nodes: [
          { id: "app", label: "Event app" },
          { id: "admin", label: "Admin panel" } ] },
      { label: "Service", nodes: [{ id: "api", label: "API server" }] },
      { label: "Backend", nodes: [
          { id: "fs", label: "Document store" },
          { id: "storage", label: "File storage" },
          { id: "fcm", label: "Push delivery" } ] },
    ],
    edges: [
      ["app", "api"], ["admin", "api"],
      ["api", "fs"], ["api", "storage"], ["api", "fcm", "push"],
    ],
  },

  "comic-con-africa": {
    layers: [
      { label: "Client", nodes: [{ id: "app", label: "Event app" }] },
      { label: "Services", nodes: [
          { id: "strapi", label: "Headless CMS" },
          { id: "cognito", label: "Identity provider" },
          { id: "fs", label: "Document store" } ] },
      { label: "Data", nodes: [
          { id: "sdb", label: "Content store" },
          { id: "howler", label: "Ticketing partner", kind: "external" },
          { id: "quests", label: "Quest state" } ] },
    ],
    edges: [
      ["app", "strapi"], ["app", "cognito"], ["app", "fs"],
      ["strapi", "sdb"], ["strapi", "howler", "tickets"], ["fs", "quests"],
    ],
  },

  "cricut-ambassador": {
    layers: [
      { label: "Client", nodes: [{ id: "app", label: "Ambassador app" }] },
      { label: "Services", nodes: [
          { id: "strapi", label: "Headless CMS" },
          { id: "regional", label: "Regional APIs" },
          { id: "fb", label: "Realtime backend" } ] },
      { label: "Data", nodes: [
          { id: "sdb", label: "Content store" },
          { id: "push", label: "Push delivery" },
          { id: "wallet", label: "Bank payouts", kind: "external" } ] },
    ],
    edges: [
      ["app", "strapi"], ["app", "regional"], ["app", "fb"],
      ["strapi", "sdb"], ["fb", "push"], ["regional", "wallet", "payouts"],
    ],
  },

  "vaya-x": {
    layers: [
      { label: "Client", nodes: [
          { id: "app", label: "Rider & driver" },
          { id: "dash", label: "Fleet dashboard" } ] },
      { label: "Services", nodes: [
          { id: "fns", label: "Serverless functions" },
          { id: "fs", label: "Document store" },
          { id: "maps", label: "Maps provider", kind: "external" } ] },
      { label: "External", nodes: [
          { id: "pay", label: "Payment gateways", kind: "external" },
          { id: "push", label: "Notifications" } ] },
    ],
    edges: [
      ["app", "fns"], ["app", "fs"], ["app", "maps"],
      ["dash", "fns"], ["dash", "fs"],
      ["fns", "pay", "checkout"], ["fns", "push"],
    ],
  },

  joj: {
    layers: [
      { label: "Client", nodes: [{ id: "app", label: "Festival app" }] },
      { label: "Services", nodes: [
          { id: "api", label: "API server" },
          { id: "fs", label: "Document store" } ] },
      { label: "External", nodes: [
          { id: "howler", label: "Ticketing partner", kind: "external" },
          { id: "push", label: "Notifications" } ] },
    ],
    edges: [
      ["app", "api"], ["app", "fs"],
      ["api", "howler", "tickets"], ["fs", "push"],
    ],
  },

  "equity-bank-portal": {
    layers: [
      { label: "Client", nodes: [{ id: "web", label: "Web portal" }] },
      { label: "Service", nodes: [
          { id: "api", label: "Application API" },
          { id: "jwt", label: "Token auth" } ] },
      { label: "Data", nodes: [
          { id: "mysql", label: "Relational store" },
          { id: "mongo", label: "Document store" },
          { id: "notify", label: "Notification service" } ] },
    ],
    edges: [
      ["web", "api"], ["web", "jwt"],
      ["api", "mysql"], ["api", "mongo"], ["api", "notify"],
    ],
  },

  "poster-builder": {
    layers: [
      { label: "Client", nodes: [
          { id: "app", label: "Editor app" },
          { id: "canvas", label: "Canvas engine" } ] },
      { label: "Services", nodes: [
          { id: "fns", label: "Serverless functions" },
          { id: "fb", label: "Auth & datastore" } ] },
      { label: "External", nodes: [
          { id: "openai", label: "AI text service", kind: "external" },
          { id: "fonts", label: "Font service", kind: "external" },
          { id: "storage", label: "File storage" } ] },
    ],
    edges: [
      ["app", "fb"], ["app", "fns"], ["canvas", "fns", "download"],
      ["fns", "openai", "templates"], ["fns", "fonts"], ["fb", "storage"],
    ],
  },

  "whatsapp-bot": {
    layers: [
      { label: "User", nodes: [{ id: "wa", label: "Messaging user" }] },
      { label: "Platform", nodes: [
          { id: "meta", label: "Messaging platform", kind: "external" } ] },
      { label: "Ingress", nodes: [
          { id: "ngrok", label: "Tunnel" },
          { id: "hook", label: "Webhook endpoint" } ] },
      { label: "Bot", nodes: [
          { id: "srv", label: "HTTP server" },
          { id: "cmds", label: "Command handlers" } ] },
    ],
    edges: [
      ["wa", "meta", "message"], ["meta", "ngrok"], ["meta", "hook"],
      ["ngrok", "srv"], ["hook", "cmds"],
    ],
  },

  jaride: {
    layers: [
      { label: "Client", nodes: [
          { id: "web", label: "Web platform" },
          { id: "apps", label: "Mobile apps" } ] },
      { label: "Services", nodes: [
          { id: "api", label: "Service API" },
          { id: "php", label: "Application backend" } ] },
      { label: "Data", nodes: [
          { id: "db", label: "Relational store" },
          { id: "pay", label: "Payments & credits", kind: "external" } ] },
    ],
    edges: [
      ["web", "api"], ["web", "php"], ["apps", "api"],
      ["api", "db"], ["api", "pay"], ["php", "db"],
    ],
  },

  jacasa: {
    layers: [
      { label: "Client", nodes: [{ id: "web", label: "Web platform" }] },
      { label: "Services", nodes: [
          { id: "listings", label: "Listings API" },
          { id: "booking", label: "Booking service" },
          { id: "geo", label: "Location search" } ] },
      { label: "Data", nodes: [{ id: "db", label: "Relational store" }] },
    ],
    edges: [
      ["web", "listings"], ["web", "booking"], ["web", "geo"],
      ["listings", "db"], ["booking", "db"], ["geo", "db"],
    ],
  },

  "jaride-eat": {
    layers: [
      { label: "Client", nodes: [{ id: "web", label: "Web storefront" }] },
      { label: "Services", nodes: [
          { id: "orders", label: "Ordering API" },
          { id: "inv", label: "Inventory service" } ] },
      { label: "Data", nodes: [
          { id: "db", label: "Relational store" },
          { id: "pay", label: "Payments", kind: "external" } ] },
    ],
    edges: [
      ["web", "orders"], ["web", "inv"],
      ["orders", "db"], ["orders", "pay", "checkout"], ["inv", "db"],
    ],
  },
};

export const architectureFor = (id) => architectures[id];
